import { getInitData } from './telegram';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

export interface MeResponse {
  subscription: {
    state: 'new' | 'trial' | 'active' | 'expired';
    expires_at: string | null;
    days_left: number;
    url: string;
    base64: string;
  };
  user: {
    telegram_id: string;
    first_name: string;
    registered_at: string;
  };
  history: {
    had_trial: boolean;
    had_paid: boolean;
  };
  pricing: PricingSnapshot;
  links: LinksBlock;
}

export interface LinksBlock {
  // t.me deeplink to the support bot with the caller's Telegram ID injected
  // as the start parameter. Always populated by the backend.
  support_bot_url: string;
  // User-agreement URL. Empty string when TERMS_URL isn't configured on the
  // deployment — the profile screen hides the row in that case.
  terms_url: string;
}

export interface PricingSnapshot {
  duration_days: number;
  stars: {
    base_amount: number;
    final_amount: number;
    discount_pct: number;
    is_first_time: boolean;
  };
  ton: {
    enabled: boolean;
    base_usd_cents: number;
    final_usd_cents: number;
    discount_pct: number;
    is_first_time: boolean;
  };
}

export interface StarsInvoice {
  invoice_url: string;
  amount_stars: number;
  base_amount_stars: number;
  discount_pct: number;
}

export interface TonInvoice {
  order_id: string;
  // pay_link is the t.me/CryptoBot?start=… chat deeplink. Kept as a fallback
  // for hosts (older Telegram, plain browser dev) that can't open the mini
  // app variant.
  pay_link: string;
  // mini_app_pay_link is the t.me/CryptoBot/app?startapp=… deeplink that
  // opens Crypto Pay's mini app on top of ours. Preferred — keeps the user
  // inside Telegram chrome instead of bouncing through the in-app browser.
  // May be an empty string if Crypto Pay didn't return one.
  mini_app_pay_link: string;
  amount_usd_cents: number;
  expires_at: string;
}

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const initData = getInitData();
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (initData) {
    headers.set('Authorization', `tma ${initData}`);
  }

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const text = await res.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      // non-JSON body: surface as plain text in the error
    }
  }

  if (!res.ok) {
    const errBody = body as { error?: string } | null;
    throw new ApiError(res.status, errBody?.error ?? 'http_error', text || res.statusText);
  }

  return body as T;
}

// Idempotent account creation for the identity in signed Telegram initData.
export function ensureRegistered(): Promise<void> {
  return request<void>('/api/v1/miniapp/register', { method: 'POST' });
}

export function fetchMe(signal?: AbortSignal): Promise<MeResponse> {
  return request<MeResponse>('/api/v1/miniapp/me', { signal });
}

export function createStarsInvoice(): Promise<StarsInvoice> {
  return request<StarsInvoice>('/api/v1/miniapp/invoice/stars', { method: 'POST' });
}

export function createTonInvoice(): Promise<TonInvoice> {
  return request<TonInvoice>('/api/v1/miniapp/invoice/ton', { method: 'POST' });
}

export interface TrialStartResult {
  subscription_id: string;
  expires_at: string;
}

// Triggers a fresh trial subscription for the calling user. Backend returns
// 409 (ApiError code "trial_already_used") when the user has already had any
// prior subscription; the onboarding screen handles that by routing to home
// without surfacing an error.
export function startTrial(): Promise<TrialStartResult> {
  return request<TrialStartResult>('/api/v1/miniapp/trial', { method: 'POST' });
}
